import { createStart, createMiddleware } from "@tanstack/react-start";
import { useSession } from "@tanstack/react-start/server";

import { renderErrorPage } from "./lib/error-page";
import { betaSessionConfig, isPublicPath, type BetaSession } from "./lib/beta-session";
import { isKeys4SteamHost, isKeys4SteamPreviewRequest } from "./lib/site-brand";

const errorMiddleware = createMiddleware().server(async ({ next }) => {
  try {
    return await next();
  } catch (error) {
    if (error != null && typeof error === "object" && "statusCode" in error) {
      throw error;
    }
    console.error(error);
    return new Response(renderErrorPage(), {
      status: 500,
      headers: { "content-type": "text/html; charset=utf-8" },
    });
  }
});

/**
 * Private-beta gate: every request that isn't a public path (login, logout,
 * static assets, backend API) requires an unlocked session cookie. Users are
 * redirected to /login with the originally requested path preserved.
 */
const betaGateMiddleware = createMiddleware().server(async ({ next, request }) => {
  const url = new URL(request.url);
  const pathname = url.pathname;
  const host = request.headers.get("x-forwarded-host") ?? request.headers.get("host") ?? url.host;

  if (isPublicPath(pathname)) return next();
  // The real Keys4Steam hosts are intentionally public landing pages.
  if (pathname === "/" && isKeys4SteamHost(host)) return next();
  // Development-only visual QA. The dedicated path avoids relying on query
  // parameters or forwarded host headers being available at the first guard.
  if (import.meta.env.DEV && pathname === "/keys4steam-preview") return next();
  if (import.meta.env.DEV && pathname === "/" && isKeys4SteamPreviewRequest(host, url)) return next();

  const session = await useSession<BetaSession>(betaSessionConfig());
  if (session.data?.unlocked) return next();

  const redirectTo = `${pathname}${url.search}`;
  return new Response(null, {
    status: 302,
    headers: {
      Location: `/login?redirect=${encodeURIComponent(redirectTo)}`,
      "cache-control": "no-store",
    },
  });
});

export const startInstance = createStart(() => ({
  requestMiddleware: [errorMiddleware, betaGateMiddleware],
}));
